import { Box, Grid } from '@mui/material'
import { updateEmailMarketingCampaign } from 'api/digitalMarketing'
import { EmailMarketingCampaign } from 'api/models'
import { Toast } from 'components'
import RightDrawer from 'components/RightDrawer/RightDrawer'
import { getToDoContentType } from 'features/digital-marketing/ToDo/components'
import { ButtonLoadingIcon } from 'features/digital-marketing/ToDo/FeedbackModal/styles'
import moment from 'moment'
import { useState } from 'react'
import { useAuth0 } from 'libs/auth0-react'

import _ from 'lodash'
import { EmailMarketingCampaignInput } from 'types/digitalMarketing/campaigns'
import { TodoItemWithProperties } from 'types/digitalMarketing/common'

import {
  ActionButtonsContainer,
  Container,
  DetailsContainerBox,
  GreenButton,
  HTMLContainerBox,
  RedButton,
  StyledCampaignPreviewTitle,
  StyledDetailsContainer,
  StyledLabel,
  StyledLabelContainer,
  StyledTextField,
  StyledTypography,
  StyledApprovalLink,
} from './styles'
import { CampaignStatusBadge } from 'features/digital-marketing/EmailMarketingCampaigns/components'
import InfoIcon from '@mui/icons-material/Info';
import { APPROVER_APP_NAME } from 'utils/constant'
interface EmailMarketingDetailsDrawerProps {
  open: boolean
  item: TodoItemWithProperties | EmailMarketingCampaign | null
  onClose: (item?: TodoItemWithProperties) => void
}

const EmailMarketingDetailsDrawer: React.FC<EmailMarketingDetailsDrawerProps> = ({
  open,
  item,
  onClose,
}) => {
  const { user } = useAuth0()
  const [additionalFeedback, setAdditionalFeedback] = useState('')
  const [loadingStatus, setLoadingStatus] = useState<{ approve: boolean; reject: boolean }>({
    approve: false,
    reject: false,
  })

  const filteredMagicLinks = (item as EmailMarketingCampaign)?.magic_link?.filter((link) => !!link.email)

  const notifyError = (errorMsg: string) => {
    Toast.error({
      message: `We couldn't process your feedback, please try again. Error: ${errorMsg}`,
    })
    return
  }

  const handleOnAction = async (item: TodoItemWithProperties, isApproved: boolean) => {
    if (item?.organizationId) {
      try {
        setLoadingStatus((prev) => ({ ...prev, [isApproved ? 'approve' : 'reject']: true }))
        const emailMarketingBody: EmailMarketingCampaignInput = {
          status: isApproved ? 'approved' : 'rejected',
          feedback: additionalFeedback,
          approver: user?.email || user?.sub || 'Unknown',
          approver_app: APPROVER_APP_NAME,
        }
        await updateEmailMarketingCampaign(item?.organizationId, item.id, emailMarketingBody)
        const message = isApproved
          ? `${getToDoContentType(item)} has been approved and will publish soon.`
          : `${getToDoContentType(item)} has been rejected.`

        if (onClose) {
          onClose(item)
        }

        Toast.success({ message }, { autoClose: 5000, hideProgressBar: true })
      } catch (error) {
        let errorMessage = (error as Error).message

        const errorData = (error as any).response?.data?.errors

        if (errorData) {
          try {
            const errors = JSON.parse(errorData)
            errorMessage = errors?.error || errorMessage
            return notifyError(errorMessage)
          } catch (innerError) {
            return notifyError((error as Error).message)
          }
        } else {
          return notifyError('An unexpected error occurred.')
        }
      } finally {
        setLoadingStatus((prev) => ({ ...prev, [isApproved ? 'approve' : 'reject']: false }))
      }
    }
  }

  const getEmailDetailsBlock = (campaign: EmailMarketingCampaign) => {
    return (
      <>
        <StyledDetailsContainer>
          <StyledTypography variant='body1'>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <StyledLabel>Status:</StyledLabel>
              <CampaignStatusBadge status={campaign?.status} />
            </Box>
          </StyledTypography>
          {campaign?.approver && (
            <StyledTypography variant='body1'>
              <StyledLabel>Decision:</StyledLabel> {campaign.approver} using {campaign.approver_app} on{' '}
              {moment(campaign.approved_at).format('MM/DD/YY h:mm A')}
            </StyledTypography>
          )}
          {filteredMagicLinks && filteredMagicLinks.length > 0 && (
            <>
              <StyledTypography variant='body1'>
                <StyledLabel>Approval Links:</StyledLabel>
              </StyledTypography>
              {filteredMagicLinks.map((link, index) => (
                <StyledTypography key={index} variant='body1'>
                  {(!['approved', 'rejected'].includes(campaign.status) && link.link) ? (
                    <Box component="span" sx={{ ml: 1 }} data-testid="magic-link-container">
                      <StyledApprovalLink href={link.link} target="_blank" rel="noopener noreferrer" data-testid="approval-link">{link.email}</StyledApprovalLink> - {_.startCase(link.state)}
                    </Box>
                  ) : (
                    <Box component="span" sx={{ ml: 1 }} data-testid="magic-link-container">
                      {link.email} - {_.startCase(link.state)}
                    </Box>
                  )}
                </StyledTypography>
              ))}
            </>
          )}
        </StyledDetailsContainer>
        <StyledDetailsContainer>
          <StyledTypography variant='body1'>
            <StyledLabel>Schedule:</StyledLabel>{' '}
            {moment(campaign?.scheduled_at).format('dddd, MM/DD/YYYY, h:mm A')}
          </StyledTypography>
          <StyledTypography variant='body1'>
            <StyledLabel>Subject:</StyledLabel> {campaign?.subject}
          </StyledTypography>
          {campaign?.from && (
            <StyledTypography variant='body1'>
              <StyledLabel>From:</StyledLabel> {campaign?.from}
            </StyledTypography>
          )}
          {campaign?.to && (
            <StyledTypography variant='body1'>
              <StyledLabel>To:</StyledLabel> {campaign?.to}
            </StyledTypography>
          )}
        </StyledDetailsContainer>
        <StyledDetailsContainer style={{ display: 'flex' }}>
          <InfoIcon fontSize='small' sx={{ marginRight: '20px', fill: 'grey' }} />
          <StyledTypography variant='body1' style={{ padding: '0px 0px 0px 0px' }}>
            Need to reschedule this campaign? Click 'Reject' and provide a new send date in the feedback.
          </StyledTypography>
        </StyledDetailsContainer>
        <StyledLabelContainer variant='body1'>
          <StyledLabel>Feedback:</StyledLabel>
        </StyledLabelContainer>
        <StyledTextField
          InputProps={{
            rows: 5,
            multiline: true,
            inputComponent: 'textarea',
          }}
          value={additionalFeedback || campaign?.feedback || ''}
          onChange={(e) => setAdditionalFeedback(e.target.value)}
          disabled={campaign?.status !== 'pending_approval'}
        />
        {campaign?.status === 'pending_approval' && (
          <ActionButtonsContainer>
            <RedButton
              variant='contained'
              endIcon={<ButtonLoadingIcon size={16} loading={loadingStatus.reject} />}
              onClick={() => handleOnAction(item as TodoItemWithProperties, false)}
              disabled={loadingStatus.reject || loadingStatus.approve}
            >
              Reject
            </RedButton>
            <GreenButton
              variant='contained'
              onClick={() => handleOnAction(item as TodoItemWithProperties, true)}
              endIcon={<ButtonLoadingIcon size={16} loading={loadingStatus.approve} />}
              disabled={loadingStatus.reject || loadingStatus.approve}
            >
              Approve
            </GreenButton>
          </ActionButtonsContainer>
        )}
      </>
    )
  }
  return (
    <RightDrawer
      isOpen={open}
      onClose={() => onClose()}
      title={(item as EmailMarketingCampaign)?.name}
      margin='0'
      hideBackdrop
    >
      <Container container spacing={0}>
        <Grid item xs={12}>
          <DetailsContainerBox>
            <Box
              sx={{
                padding: 0,
                marginBottom: '40px',
              }}
            >
              {getEmailDetailsBlock(item as EmailMarketingCampaign)}
            </Box>
          </DetailsContainerBox>
        </Grid>
        <Grid item xs={12}>
          <StyledCampaignPreviewTitle>Campaign Preview</StyledCampaignPreviewTitle>
          <HTMLContainerBox>
            <div
              dangerouslySetInnerHTML={{
                __html: (item as EmailMarketingCampaign)?.preview || '',
              }}
            />
          </HTMLContainerBox>
        </Grid>
      </Container>
    </RightDrawer>
  )
}

export default EmailMarketingDetailsDrawer
